// Components
import React, { Component } from 'react';
import { Popover, OverlayTrigger } from 'react-bootstrap';
import { PropTypes } from 'prop-types';
import PageHeader from '../components/PageHeader';
import CreateGroupButton from '../components/CreateGroupButton';
import GroupDetails from '../components/GroupDetails';
import DeleteGroupButton from '../components/DeleteGroupButton';
import LoadingSpinner from '../components/LoadingSpinner';
import ManageGroupPoliciesButton from '../components/ManageGroupPoliciesButton';
import { namespace } from '../../../config.json';
// External
import Reactable from 'reactable';

// To handle requests
import GroupsAPI from '../utils/GroupsAPI';

// CSS
import '../components/Panel.css';
import '../pages/ResponsiveTable.css';
import brandingStyles from '../branding/brandingStyles';

// Setting reactable values instead of regular table
const Table = Reactable.Table;
const Thead = Reactable.Thead;
const Th = Reactable.Th;
const Tr = Reactable.Tr;
const Td = Reactable.Td;

// Tools
import copyToClipboard from '../utils/CopyToClipboard';
import tableSortingTool from '../utils/tableSortingTool';
import compareDates from '../utils/compareDates';

class GroupsComponent extends Component {

    constructor() {
        super();

        const nbItemsPagination = JSON.parse(
           localStorage.getItem('scality-nbitems-groups')
        );
        this.state = {
            groups: [],
            filterText: '',
            selectedGroup: null,
            currentIndex: 0,
            maxIndex: -1,
            marker: null,
            nbItems: nbItemsPagination || 20,
            dataLoaded: false,
        };
        // We need to bind this to functions so we can have
        // the proper this reference inside the method
        this.handleFilterChange = this.handleFilterChange.bind(this);
        this.handlePreviousPage = this.handlePreviousPage.bind(this);
        this.handleNextPage = this.handleNextPage.bind(this);
        this.handleUserAction = this.handleUserAction.bind(this);
        this.handleGroupCreation = this.handleGroupCreation.bind(this);
        this.getGroupRow = this.getGroupRow.bind(this);
        this.refresh = this.refresh.bind(this);
        this.setSelected = this.setSelected.bind(this);
        this.sortTable = this.sortTable.bind(this);
        this.openPoliciesManagement = this.openPoliciesManagement.bind(this);
        this.closePoliciesManagement = this.closePoliciesManagement.bind(this);
        this.mounted = false;
    }

    getGroupRow(group, index) {
        const actGroup = <div className="btn-group btn-group-xs group-actions">
            <DeleteGroupButton
              groupname={group.GroupName}
              callback={this.handleUserAction}
            />
        </div>;

        const groupDate = new Date(group.CreateDate);

        const arnPopover = (
            <Popover
              id="popover-group-arn-value"
              title="Amazon Resource Name (ARN)"
            >
              <p style={{ wordWrap: 'break-word' }}>
                  {group.Arn}
              </p>
              <hr />
              <strong>
                {this.state.copiedState
                  ? 'Copied' : 'Click to copy to clipboard'}
              </strong>
            </Popover>
        );

        const arnEyeIcon = (
            <OverlayTrigger
                trigger={['hover', 'focus']}
                placement="top"
                overlay={arnPopover}>
                    <i
                      className="fa fa-eye"
                      style={Object.assign(
                          {
                              fontSize: '18px',
                              cursor: 'pointer',
                          },
                        brandingStyles.mainColor
                      )}
                      onClick={this.copyArnToClipboard.bind(this, group.Arn)}
                    />
            </OverlayTrigger>
        );


        return (
            <Tr key={group.GroupId}
              className="group-row clickable"
              style={
                index % 2 === 0
                ? brandingStyles.backgrounds.panelSecondary
                : brandingStyles.backgrounds.panel
              }
              onClick={this.setSelected.bind(this, group)}
            >
                <Td column="GroupName"
                  className="padding-l-25 bold"
                >
                    {group.GroupName}
                </Td>
                <Td column="Arn" style={{
                    fontStyle: 'italic',
                    wordWrap: 'break-word',
                }}>
                    {arnEyeIcon}
                </Td>
                <Td column="CreateDate">
                    {groupDate.toDateString()}
                </Td>
                <Td column="Actions">
                    {actGroup}
                </Td>
            </Tr>
        );
    }

    componentDidMount() {
        this.mounted = true;
        this.refresh();
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    // To handle table filter change
    handleFilterChange(e) {
        if (this.mounted) {
            this.setState({ filterText: e.target.value });
        }
    }

    handlePreviousPage() {
        if (this.mounted) {
            this.setState({
                currentIndex: this.state.currentIndex - 1,
            });
        }
    }

    handleNextPage() {
        const currentIndex = this.state.currentIndex;
        if (this.mounted) {
            this.setState({
                currentIndex: this.state.currentIndex + 1,
            });
        }
        if (currentIndex + 1 === this.state.groups.length) {
            this.refresh();
        }
    }

    handleUserAction() {
        if (this.mounted) {
            this.setState({
                groups: [],
                filterText: '',
                currentIndex: 0,
                maxIndex: -1,
                selectedGroup: null,
                marker: null,
            }, this.refresh);
        }
    }

    handleGroupCreation(groupName) {
        if (this.mounted) {
            this.setState({
                groups: [],
                filterText: '',
                currentIndex: 0,
                maxIndex: -1,
                selectedGroup: null,
                marker: null,
            }, () => {
                this.refresh();
                this.openPoliciesManagement(groupName);
            });
        }
    }

    openPoliciesManagement(groupName) {
        if (this.mounted) {
            this.setState({
                managedGroup: groupName,
            });
        }
    }

    closePoliciesManagement(groupName) {
        if (this.mounted) {
            this.setState({
                managedGroup: groupName,
            });
        }
    }

    handleNbItemsSelect(event) {
        localStorage.setItem('scality-nbitems-groups',
        JSON.stringify(event.target.value));
        if (this.mounted) {
            this.setState({
                nbItems: event.target.value,
            }, this.handleUserAction);
        }
    }

    copyArnToClipboard(value) {
        const success = copyToClipboard(value);
        this.setCopied(success);
    }

    setCopied(success) {
        if (success && this.mounted) {
            this.setState({ copiedState: true });
            setTimeout(() => {
                this.setState({ copiedState: false });
            }, 2000);
        }
    }

    // Set selected group
    setSelected(selectedGroup) {
        if (this.mounted) {
            this.setState({ selectedGroup });
        }
    }

    // Refresh users list
    refresh() {
        GroupsAPI.getGroups(this.state.nbItems, this.state.marker).then(res => {
            const groupPages = this.state.groups;
            groupPages.push(res.groups);
            const toState = {
                groups: groupPages,
                marker: res.marker,
                maxIndex: res.marker ? -1 : this.state.currentIndex,
                dataLoaded: true,
            };
            // If a selected group is specified in the URL
            if (this.props.match && this.props.match.params.selected) {
                // We search for the equivalent group in the API list
                const selectedObject = groupPages[this.state.currentIndex]
                .filter(obj => (
                    obj.GroupName === this.props.match.params.selected
                ));
                // if found we set it in the state
                toState.selectedGroup = selectedObject.length
                    ? selectedObject[0]
                    : null;
                if (this.mounted) {
                    this.setState(toState);
                }
                if (!toState.selectedGroup && toState.marker && this.mounted) {
                    this.setState({
                        currentIndex: this.state.currentIndex + 1,
                    });
                    this.refresh();
                    return;
                } else if (!toState.selectedGroup && !toState.marker
                  && this.mounted) {
                    this.setState({
                        currentIndex: 0,
                    });
                }
            }
            // If the selected group has been deleted we unselect it
            if (groupPages[this.state.currentIndex]
              .indexOf(this.state.selectedGroup) === -1) {
                toState.selectedGroup = null;
            }
            if (this.mounted) {
                this.setState(toState);
            }
        }).catch(message => {
            if (message.status === 401 || message.status === 403) {
                window.location.href =
                    `${namespace}/login/error/${message.status}`;
            } else if (this.mounted) {
                this.setState({ errorMessage: message.response.body.error });
            }
        });
    }

    sortTable(sortingRule) {
        const sorted = tableSortingTool(
          this.state.groups[this.state.currentIndex],
          sortingRule
        );
        const current = this.state.groups;
        current[this.state.currentIndex] = sorted;
        if (this.mounted) {
            this.setState({ groups: current });
        }
    }

    render() {
        let errorAlert;
        if (this.state.errorMessage) {
            errorAlert = (
              <div className="alert alert-warning">
                <strong>Error : </strong>
                {this.state.errorMessage}
              </div>
            );
        }

        const noGroupsStyle = {
            textAlign: 'center',
            color: '#bdbdbd',
        };

        let groupsList;
        const currentGroups = this.state.groups[this.state.currentIndex];
        if (currentGroups) {
            // Map over the contacts and get an element for each of them
            groupsList = currentGroups.map(
              (group, index) => this.getGroupRow(group, index)
            );
        }

        const arnPopover = (
            <Popover id="popover-user-arn" title="Amazon Resource Name (ARN)">
                <p>
                    {'Each IAM user has a unique ARN with the format: '}
                </p>
                <p>
                    {'arn:<partition>:<service>::<account-id>:'}
                    {'<resourcetype/resource>'}
                </p>
            </Popover>
        );

        const arnHelpIcon = (
            <OverlayTrigger
                trigger={['hover', 'focus']}
                placement="right"
                overlay={arnPopover}>
                    <i className="fa fa-question-circle-o" />
            </OverlayTrigger>
        );

        const table = <Table
          className="table table-striped"
          id="table"
          sortable={[
              'GroupName',
              {
                  column: 'CreateDate',
                  sortFunction: compareDates,
              },
          ]}
          onSort={this.sortTable}
          filterBy={this.state.filterText}
          hideFilterInput
          filterable={['Group name', 'Arn', 'Created']}>
            <Thead>
                <Th column="GroupName" style={{
                    paddingLeft: '25px',
                }}>
                    <strong className="name-header">Group name</strong>
                </Th>
                <Th column="Arn">
                    <strong className="name-header">ARN</strong>
                    {' '}{arnHelpIcon}
                </Th>
                <Th column="CreateDate">
                    <strong className="name-header">Created on</strong>
                </Th>
                <Th column="Actions">
                    <strong className="name-header">Actions</strong>
                </Th>
            </Thead>
            {groupsList}
        </Table>;

        let rightPanel;
        // No group is selected
        if (!this.state.selectedGroup) {
            rightPanel = <h3 style={noGroupsStyle}>
                No group selected.
                <br/>
                Click on a group to display its details.
            </h3>;
        } else {
            rightPanel = <GroupDetails
              group={this.state.selectedGroup}
              unselect={this.setSelected}/>;
        }

        const prevDisabled = this.state.currentIndex === 0;
        const nextDisabled = this.state.currentIndex === this.state.maxIndex;

        const pagerInstance = (
          <ul className="pager padding-l-15 padding-r-15">
            <li
              className={prevDisabled ? 'previous disabled' : 'previous'}
            >
              <a
                className="clickable"
                onClick={this.handlePreviousPage}
                style={brandingStyles.buttons.primary}
              >&larr;</a>
            </li>
            <li
              className={nextDisabled ? 'next disabled' : 'next'}
            >
              <a
                className="clickable"
                onClick={this.handleNextPage}
                style={brandingStyles.buttons.primary}
              >&rarr;</a>
            </li>
          </ul>
        );

        let manageCreatedModal;
        if (this.state.managedGroup) {
            manageCreatedModal = (
              <ManageGroupPoliciesButton
                groupName={this.state.managedGroup}
                controlled={true}
                show={this.state.managedGroup.length > 0}
                callback={this.closePoliciesManagement}
              />
            );
        }

        return (
            <div id="page-wrapper" style={brandingStyles.backgrounds.main}>
                <PageHeader facode="users" title="Groups Management"/>
                {errorAlert}
                <div className="col col-md-6 group-list">
                    <div
                      className="panel panel-default"
                      style={brandingStyles.backgrounds.panel}
                    >
                        <div className="panel-body" style={{
                            padding: '0px',
                        }}>
                            <div style={{
                                padding: '15px',
                            }}>
                              <div style={{
                                  display: 'inline-block',
                              }}>
                                <CreateGroupButton
                                  callback={this.handleGroupCreation}
                                />
                              {manageCreatedModal}
                              </div>
                              <select
                              className="pull-right form-control
                              pagination-select"
                              style={brandingStyles.backgrounds.input}
                              value={this.state.nbItems}
                              onChange={this.handleNbItemsSelect.bind(this)}>
                                <option value="20">20</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                              </select>
                            </div>
                            <input
                              className="form-control full-width"
                              style={brandingStyles.backgrounds.input}
                              placeholder="Find groups"
                              value={this.state.filterText}
                              onChange={this.handleFilterChange}></input>
                            <div className="table-break">
                              {this.state.dataLoaded
                                ? table : <LoadingSpinner />}
                            </div>
                            {pagerInstance}
                        </div>
                    </div>
                </div>

                <div className="col col-md-6 group-users">
                    {rightPanel}
                </div>
            </div>
        );
    }
}

GroupsComponent.propTypes = {
    match: PropTypes.object,
};

export default GroupsComponent;



/** WEBPACK FOOTER **
 ** ./src/pages/Groups.js
 **/